import axios, { AxiosResponse } from 'axios';
import { AuthRequest, VerifyOTPRequest, ApiResponse, User } from '../types';
import config from '../config.env';

// Create axios instance with base configuration
const authClient = axios.create({
  baseURL: config?.API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

// Request interceptor for logging
authClient.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Auth API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('Auth API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
authClient.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Auth API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    console.error('Auth API Response Error:', error.response?.data || error.message);
    
    // Handle 401 errors by clearing any stored auth state
    if (error.response?.status === 401) {
      // Session expired or invalid - handled by server
    }
    
    return Promise.reject(error);
  }
);

// Auth API Service
export const authApi = {
  async requestOTP(data: AuthRequest): Promise<{ sessionId: string; expiresIn: number }> {
    try {
      const response: AxiosResponse<ApiResponse<{ sessionId: string; expiresIn: number }>> = 
        await authClient.post('/auth/request-otp', data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error || 'Failed to request OTP');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || error.message);
      }
      throw error;
    }
  },

  async verifyOTP(data: VerifyOTPRequest): Promise<{ user: User }> {
    try {
      const response: AxiosResponse<ApiResponse<{ user: User }>> = 
        await authClient.post('/auth/verify-otp', data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error || 'Failed to verify OTP');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || error.message);
      }
      throw error;
    }
  },

  async getMe(): Promise<{ user: User }> {
    try {
      const response: AxiosResponse<ApiResponse<{ user: User }>> = 
        await authClient.get('/auth/me');
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error || 'Failed to get user info');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || error.message);
      }
      throw error;
    }
  },

  async logout(): Promise<void> {
    try {
      await authClient.post('/auth/logout');
    } catch (error) {
      // Logout can fail silently since it's mainly server-side
      console.warn('Logout request failed:', error);
    }
  },
};

export default authApi;
